"use client"

import { JSX, memo, useCallback, useEffect, useState } from "react"
import type { ReactNode } from "react"
import { useDataAttributes } from "../../hooks"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../shadcn/tabs"
import { TIcon } from "./TIcon"
import { TIconNameV2 } from "./TIconPathsV2"

/**
 * @description 개별 탭 패널 데이터
 */
interface ITabPanel {
	/** 탭 버튼에 표시되는 라벨 */
	label: string
	/** 탭 식별 값 */
	value: string
	/** 탭 선택 시 표시되는 패널 콘텐츠 */
	pane: JSX.Element
	/** 탭 비활성화 */
	disabled?: boolean
	/** 에러 상태 표시 (경고 아이콘) */
	error?: boolean
	/** 탭 라벨 앞 아이콘 */
	icon?: TIconNameV2
	/** 탭에 숫자 뱃지 표시 */
	badge?: number
}

/**
 * @description 탭 네비게이션. panes 배열로 탭+패널 구성
 * @example
 * <TTabs
 *   panes={[
 *     { label: "탭 1", value: "tab1", pane: <div>내용 1</div> },
 *     { label: "탭 2", value: "tab2", pane: <div>내용 2</div>, badge: 3 },
 *   ]}
 *   variant="standard"
 *   size="medium"
 * />
 */
interface TTabsProps {
	/** 현재 선택된 탭 값 (제어 모드) */
	value?: string
	/** 탭 패널 배열 */
	panes: Array<ITabPanel>
	/**
	 * 탭 크기
	 * @default "medium"
	 */
	size?: "medium" | "small"
	/**
	 * 탭 정렬 방식
	 * @default "standard"
	 * @ai-style "standard" - 탭 왼쪽 정렬
	 * @ai-style "centered" - 탭 중앙 정렬
	 * @ai-style "fullWidth" - 탭 전체 너비 균등 분배
	 */
	variant?: "standard" | "centered" | "fullWidth"
	/** 탭 리스트 우측에 추가 액션 요소 */
	renderAction?: ReactNode
	/** 추가 CSS 클래스 */
	className?: string
	/** @deprecated data-* 속성을 직접 사용하세요 */
	dataAttributes?: Record<string, string | number | boolean>
	/** 탭 변경 콜백 */
	onChange?: (value: string) => void
	/**
	 * 모든 탭 패널을 DOM에 유지하여 비활성 탭의 상태를 보존
	 * - true: 비활성 탭 패널도 마운트 유지 (display:none 처리), 상태/스크롤 위치 보존
	 * - false: 기본 Radix 동작 (활성 탭만 마운트)
	 * @default false
	 */
	keepMounted?: boolean
}

export const TTabs = memo(function TTabs(props: TTabsProps): JSX.Element | null {
	const { value, panes, size = "medium", variant = "standard", renderAction, className, dataAttributes, onChange, keepMounted = false, ...restProps } = props
	const [selectedValue, setSelectedValue] = useState(() => value ?? panes[0]?.value ?? "")
	const dataProps = useDataAttributes(dataAttributes)

	useEffect(() => {
		if (value !== undefined && value !== null) {
			setSelectedValue(value)
		}
	}, [value])

	const handleChange = useCallback(
		(newValue: string) => {
			setSelectedValue(newValue)
			onChange?.(newValue)
		},
		[onChange]
	)

	if (!selectedValue) {
		return null
	}

	return (
		<Tabs className={className} value={selectedValue} onValueChange={handleChange} {...restProps} {...dataProps}>
			<TabsList data-size={size} data-variant={variant} className="tab-list">
				{panes.map((pane) => (
					<TabsTrigger key={pane.value} value={pane.value} disabled={pane.disabled} data-size={size}>
						<div className="trigger-warp">
							{pane.icon && <TIcon className="prefix" name={pane.icon} />}
							<span>{pane.label}</span>
							{pane.error && <TIcon className="suffix" size={16} color="error" name="warning" />}
							{pane.badge !== undefined && <div className="badge suffix">{pane.badge}</div>}
						</div>
					</TabsTrigger>
				))}
				{renderAction && <div className="render-action">{renderAction}</div>}
			</TabsList>
			{keepMounted ? (
				<div className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
					{panes.map((pane) => (
						<TabsContent
							key={pane.value}
							value={pane.value}
							forceMount={true}
							className="flex-1 min-h-0 overflow-hidden data-[state=inactive]:invisible data-[state=inactive]:opacity-0 data-[state=inactive]:absolute data-[state=inactive]:inset-0 data-[state=inactive]:pointer-events-none"
						>
							{pane.pane}
						</TabsContent>
					))}
				</div>
			) : (
				<div className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
					{panes.map((pane) => (
						<TabsContent key={pane.value} value={pane.value} className="min-h-0 overflow-hidden">
							{pane.pane}
						</TabsContent>
					))}
				</div>
			)}
		</Tabs>
	)
})

TTabs.displayName = "TTabs"
